import { useCallback, useEffect, useMemo, useState } from "react";
import { format, eachDayOfInterval, startOfMonth, endOfMonth } from "date-fns";
import { it } from "date-fns/locale";
import Select from "react-select";
import { database, timestamp } from "../../firebase/config";
import { useCollection } from "../../hooks/useCollection";
import { downloadPresenzePdf } from "../../stampe/generatori/presenzePdf";
import { downloadPresenzeMultiPdf } from "../../stampe/generatori/presenzeMultiPdf";
import "./PresenzeCheck.css";

const selectModalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const pausaOptions = [
  { value: "", label: "Nessuna pausa" },
  { value: "0.5", label: "0.5 ore (30 min)" },
  { value: "1", label: "1 ora (60 min)" },
  { value: "1.5", label: "1.5 ore (90 min)" },
  { value: "2", label: "2 ore (120 min)" },
];

const tipoPresenzaOptions = [
  { value: "lavorativa", label: "Lavorativa" },
  { value: "ferie", label: "Ferie" },
  { value: "malattia", label: "Malattia" },
  { value: "riposo", label: "Riposo" },
];

const getTipoPresenza = (presenza) => {
  const raw = String(presenza?.tipoPresenza || "").trim();
  if (raw) return raw;
  if (presenza?.isFerie) return "ferie";
  if (presenza?.isMalattia) return "malattia";
  if (presenza?.isRiposo) return "riposo";
  return "lavorativa";
};

const getCantiereLabel = (presenza) => {
  const c = presenza?.cantiere;
  if (typeof c === "string") return c.trim();
  if (c && typeof c === "object") return String(c.nome || "").trim();
  return "";
};

const getMezzoLabel = (mezzo) => {
  if (!mezzo) return "";
  if (typeof mezzo === "string") return mezzo.trim();
  return [mezzo?.targa, mezzo?.modello].filter(Boolean).join(" - ").trim();
};

const resolveProjectId = (projectValue, projects) => {
  if (!projectValue || !(projects || []).length) return "";
  if (typeof projectValue === "object" && projectValue.id) {
    return String(projectValue.id);
  }
  const projectName =
    typeof projectValue === "string"
      ? projectValue.trim()
      : String(projectValue?.nome || "").trim();
  if (!projectName) return "";
  return (
    (projects || []).find(
      (project) =>
        project.id === projectName ||
        String(project.nome || "").trim() === projectName,
    )?.id || ""
  );
};

const resolveMezzoId = (mezzoValue, automezzi) => {
  if (!mezzoValue || !(automezzi || []).length) return "";
  if (typeof mezzoValue === "object" && mezzoValue.id) {
    return String(mezzoValue.id);
  }
  const mezzoLabel = getMezzoLabel(mezzoValue);
  if (!mezzoLabel) return "";
  return (
    (automezzi || []).find((mezzo) => {
      const sameId = mezzo.id === mezzoLabel;
      const sameTarga = String(mezzo.targa || "").trim() === mezzoLabel;
      const sameModel = String(mezzo.modello || "").trim() === mezzoLabel;
      const compoundLabel = getMezzoLabel(mezzo);
      return sameId || sameTarga || sameModel || compoundLabel === mezzoLabel;
    })?.id || ""
  );
};

const getQrTimbraturaDayKey = (timbratura, toDateFromTimestampLike) => {
  const businessDate = String(timbratura?.businessDate || "").trim();
  if (businessDate) return businessDate;
  const openedAt = toDateFromTimestampLike(timbratura?.openedAt);
  return openedAt ? format(openedAt, "yyyy-MM-dd") : "";
};

export default function PresenzeCheck() {
  const toDateFromTimestampLike = useCallback((value) => {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    if (typeof value.seconds === "number")
      return new Date(value.seconds * 1000);
    return null;
  }, []);
  const toTimeValue = useCallback(
    (value) => {
      const d = toDateFromTimestampLike(value);
      return d ? format(d, "HH:mm") : "";
    },
    [toDateFromTimestampLike],
  );
  const normalizePresenzaForPdf = (p) => {
    if (!p) return p;
    return {
      ...p,
      dataRegistrazione:
        toDateFromTimestampLike(p.dataRegistrazione) || p.dataRegistrazione,
      oraIngresso: toDateFromTimestampLike(p.oraIngresso) || p.oraIngresso,
      oraUscita: toDateFromTimestampLike(p.oraUscita) || p.oraUscita,
    };
  };

  const ordinaPresenze = (items) => {
    if (!items || items.length === 0) return [];
    return [...items]
      .map(normalizePresenzaForPdf)
      .filter((p) => p && p.dataRegistrazione)
      .sort((a, b) => {
        const da =
          toDateFromTimestampLike(a.dataRegistrazione) ||
          (a.dataRegistrazione instanceof Date ? a.dataRegistrazione : null);
        const db =
          toDateFromTimestampLike(b.dataRegistrazione) ||
          (b.dataRegistrazione instanceof Date ? b.dataRegistrazione : null);
        if (!da || !db) return 0;
        return da - db;
      });
  };
  const renderCell = (value, kind) => {
    if (value === null || value === undefined || value === "") return "-";
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return String(value);
    }

    const d = toDateFromTimestampLike(value);
    if (d) {
      if (kind === "time") return format(d, "HH:mm", { locale: it });
      return format(d, "dd/MM/yyyy HH:mm", { locale: it });
    }

    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return "-";
      }
    }

    return String(value);
  };

  const [monthFilter, setMonthFilter] = useState(() => {
    const now = new Date();
    return format(now, "yyyy-MM");
  });

  const monthOptions = useMemo(() => {
    const base = new Date();
    const out = [];
    for (let i = -24; i <= 6; i++) {
      const d = new Date(base.getFullYear(), base.getMonth() + i, 1);
      out.push({
        value: format(d, "yyyy-MM"),
        label: format(d, "MMMM yyyy", { locale: it }),
      });
    }
    return out;
  }, []);

  const monthRange = useMemo(() => {
    if (!monthFilter) return null;
    const [yearStr, monthStr] = monthFilter.split("-");
    const start = startOfMonth(
      new Date(Number(yearStr), Number(monthStr) - 1, 1),
    );
    const end = endOfMonth(start);
    return {
      start,
      end,
      startTs: timestamp.fromDate(start),
      endTs: timestamp.fromDate(end),
    };
  }, [monthFilter]);

  const presenzeQuery = useMemo(() => {
    if (!monthRange) return null;
    return [
      ["dataRegistrazione", ">=", monthRange.startTs],
      ["dataRegistrazione", "<=", monthRange.endTs],
    ];
  }, [monthRange]);

  const timbratureQrQuery = useMemo(() => {
    if (!monthRange) return null;
    return [
      ["openedAt", ">=", monthRange.startTs],
      ["openedAt", "<=", monthRange.endTs],
    ];
  }, [monthRange]);

  const { documents: users } = useCollection("users");
  const { documents: projectsData } = useCollection(
    "projects",
    null,
    null,
    null,
    false,
  );
  const { documents: automezziData } = useCollection("automezzi");
  const attivitaQuery = useMemo(() => {
    if (!monthRange) return null;
    return [
      ["date", ">=", monthRange.startTs],
      ["date", "<=", monthRange.endTs],
    ];
  }, [monthRange]);
  const { documents: presenze, error: presenzeError } = useCollection(
    presenzeQuery ? "presenze" : null,
    presenzeQuery,
    ["dataRegistrazione", "asc"],
    null,
    true,
  );
  const { documents: attivita } = useCollection(
    attivitaQuery ? "attivita" : null,
    attivitaQuery,
    ["date", "asc"],
    null,
    true,
  );
  const { documents: timbratureQr } = useCollection(
    timbratureQrQuery ? "timbrature_qr" : null,
    timbratureQrQuery,
    ["openedAt", "asc"],
    null,
    true,
  );
  const reperibilitaQuery = useMemo(() => {
    if (!monthRange) return null;
    return [
      ["dataInizio", ">=", monthRange.startTs],
      ["dataInizio", "<=", monthRange.endTs],
    ];
  }, [monthRange]);
  const { documents: reperibilitaData } = useCollection(
    reperibilitaQuery ? "reperibilita" : null,
    reperibilitaQuery,
    ["dataInizio", "asc"],
    null,
    true,
  );
  const { documents: tuttiDocumenti } = useCollection("documenti");

  const usersById = useMemo(() => {
    const map = new Map();
    (users || []).forEach((u) => map.set(u.id, u));
    return map;
  }, [users]);

  const isCessato = (u) => {
    const settore = String(u?.anagrafica?.settore || "")
      .trim()
      .toLowerCase();
    return settore === "cessati";
  };

  const hasSettore = (u) => {
    return String(u?.anagrafica?.settore || "").trim().length > 0;
  };

  const getUserLabel = (u) => {
    const nome = String(u?.anagrafica?.nome || "").trim();
    const cognome = String(u?.anagrafica?.cognome || "").trim();
    const full = `${nome} ${cognome}`.trim();
    return full || u?.displayName || u?.email || u?.id || "-";
  };

  const presenzeByUser = useMemo(() => {
    const map = new Map();
    (presenze || []).forEach((p) => {
      const uid = p.userId || p.user?.id;
      if (!uid) return;
      if (!map.has(uid)) map.set(uid, []);
      map.get(uid).push(p);
    });
    return map;
  }, [presenze]);

  const reperibilitaByUser = useMemo(() => {
    const map = new Map();
    (reperibilitaData || []).forEach((rep) => {
      const uid = rep.userId;
      if (!uid) return;
      if (!map.has(uid)) map.set(uid, []);
      map.get(uid).push(rep);
    });
    return map;
  }, [reperibilitaData]);

  const timbratureQrByUserAndDay = useMemo(() => {
    const map = new Map();
    (timbratureQr || []).forEach((t) => {
      const uid = t.userId || t.user?.id;
      if (!uid) return;
      const dayKey = getQrTimbraturaDayKey(t, toDateFromTimestampLike);
      if (!dayKey) return;
      if (!map.has(uid)) map.set(uid, new Map());
      const byDay = map.get(uid);
      const existing = byDay.get(dayKey);
      if (!existing) {
        byDay.set(dayKey, t);
        return;
      }

      const existingDate =
        toDateFromTimestampLike(existing?.openedAt) ||
        toDateFromTimestampLike(existing?.createdAt) ||
        new Date(0);
      const nextDate =
        toDateFromTimestampLike(t?.openedAt) ||
        toDateFromTimestampLike(t?.createdAt) ||
        new Date(0);

      if (nextDate >= existingDate) {
        byDay.set(dayKey, t);
      }
    });
    return map;
  }, [timbratureQr, toDateFromTimestampLike]);

  const attivitaByUserAndDay = useMemo(() => {
    const map = new Map();
    (attivita || []).forEach((activity) => {
      const uid = activity?.userId;
      const activityDate = toDateFromTimestampLike(activity?.date);
      if (!uid || !activityDate) return;
      const dayKey = format(activityDate, "yyyy-MM-dd");
      if (!map.has(uid)) map.set(uid, new Map());
      map.get(uid).set(dayKey, activity);
    });
    return map;
  }, [attivita, toDateFromTimestampLike]);

  const userSheets = useMemo(() => {
    if (!presenzeByUser || presenzeByUser.size === 0) return [];
    const out = Array.from(presenzeByUser.entries())
      .map(([userId, items]) => {
        const u = usersById.get(userId) || { id: userId };
        return { userId, user: u, presenze: items };
      })
      .filter((s) => hasSettore(s.user) && !isCessato(s.user));
    out.sort((a, b) =>
      String(getUserLabel(a.user)).localeCompare(
        String(getUserLabel(b.user)),
        "it",
        {
          sensitivity: "base",
        },
      ),
    );
    return out;
  }, [presenzeByUser, usersById]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [addingDayKey, setAddingDayKey] = useState(null);
  const [editingPresenza, setEditingPresenza] = useState(null);
  const [addForm, setAddForm] = useState({
    tipoPresenza: "lavorativa",
    oraIngresso: "",
    oraUscita: "",
    cantiere: "",
    mezzo: "",
    durataPausa: "",
    codiceMalattia: "",
    note: "",
  });
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [generatingMultiUserPdf, setGeneratingMultiUserPdf] = useState(false);
  const [allConfirmedBonuses, setAllConfirmedBonuses] = useState({});
  const [duplicatesDayKey, setDuplicatesDayKey] = useState(null);
  const [duplicatePresenze, setDuplicatePresenze] = useState([]);
  const [isDeletingDuplicate, setIsDeletingDuplicate] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);

  useEffect(() => {
    setActiveIndex(0);
  }, [monthFilter]);

  useEffect(() => {
    if (activeIndex > userSheets.length - 1) {
      setActiveIndex(0);
    }
  }, [activeIndex, userSheets.length]);

  const activeSheet = userSheets[activeIndex] || null;

  const periodo = useMemo(() => {
    if (!monthFilter) return null;
    const [yearStr, monthStr] = monthFilter.split("-");
    return { anno: Number(yearStr), mese: Number(monthStr) - 1 };
  }, [monthFilter]);

  const periodoLabel = useMemo(() => {
    if (!periodo) return "";
    return format(new Date(periodo.anno, periodo.mese, 1), "MMMM_yyyy", {
      locale: it,
    });
  }, [periodo]);

  // Recupera i bonus confermati per tutti gli utenti per il periodo selezionato
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!users || users.length === 0 || !periodo) return;
      const entries = await Promise.all(
        users.map(async (u) => {
          const docId = `${u.id}_${periodo.anno}_${periodo.mese}`;
          try {
            const snap = await database
              .collection("bonus_mensili")
              .doc(docId)
              .get();
            return [u.id, snap.exists ? snap.data().bonuses || [] : null];
          } catch (_) {
            return [u.id, null];
          }
        }),
      );
      if (!cancelled) setAllConfirmedBonuses(Object.fromEntries(entries));
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [users, periodo]);

  const getReperibilitaForUser = useCallback(
    (userId) => reperibilitaByUser.get(userId) || [],
    [reperibilitaByUser],
  );

  const daysOfMonth = useMemo(() => {
    if (!monthRange) return [];
    return eachDayOfInterval({ start: monthRange.start, end: monthRange.end });
  }, [monthRange]);

  const daysMeta = useMemo(() => {
    if (!monthRange) return [];
    return daysOfMonth.map((day) => {
      const key = format(day, "yyyy-MM-dd");
      return { day, key };
    });
  }, [daysOfMonth, monthRange]);

  const activePresenzeByDay = useMemo(() => {
    const map = new Map();
    (activeSheet?.presenze || []).forEach((p) => {
      const d = toDateFromTimestampLike(p.dataRegistrazione);
      if (!d) return;
      map.set(format(d, "yyyy-MM-dd"), p);
    });
    return map;
  }, [activeSheet, toDateFromTimestampLike]);

  const activePresenzeByDayMulti = useMemo(() => {
    const map = new Map();
    (activeSheet?.presenze || []).forEach((p) => {
      const d = toDateFromTimestampLike(p.dataRegistrazione);
      if (!d) return;
      const dayKey = format(d, "yyyy-MM-dd");
      if (!map.has(dayKey)) map.set(dayKey, []);
      map.get(dayKey).push(p);
    });
    map.forEach((items) => {
      items.sort((a, b) => {
        const aUpdated = toDateFromTimestampLike(a.updatedAt);
        const bUpdated = toDateFromTimestampLike(b.updatedAt);
        if (aUpdated && bUpdated) return bUpdated - aUpdated;
        const aCreated = toDateFromTimestampLike(a.createdAt);
        const bCreated = toDateFromTimestampLike(b.createdAt);
        if (aCreated && bCreated) return bCreated - aCreated;
        const aIn = toDateFromTimestampLike(a.oraIngresso);
        const bIn = toDateFromTimestampLike(b.oraIngresso);
        if (aIn && bIn) return aIn - bIn;
        return 0;
      });
    });
    return map;
  }, [activeSheet, toDateFromTimestampLike]);

  const activeTimbratureByDay = useMemo(() => {
    if (!activeSheet?.userId) return new Map();
    return timbratureQrByUserAndDay.get(activeSheet.userId) || new Map();
  }, [activeSheet?.userId, timbratureQrByUserAndDay]);

  const activeAttivitaByDay = useMemo(() => {
    if (!activeSheet?.userId) return new Map();
    return attivitaByUserAndDay.get(activeSheet.userId) || new Map();
  }, [activeSheet?.userId, attivitaByUserAndDay]);

  const projectOptions = useMemo(
    () =>
      (projectsData || []).map((project) => ({
        value: project.id,
        label: `${project.categoria} - ${project.nome}`,
      })),
    [projectsData],
  );

  const mezzoOptions = useMemo(
    () =>
      (automezziData || []).map((mezzo) => ({
        value: mezzo.id,
        label: getMezzoLabel(mezzo),
      })),
    [automezziData],
  );

  const missingDayKeys = useMemo(() => {
    const missing = [];
    if (!activeSheet) return missing;
    daysMeta.forEach(({ key }) => {
      if (!activePresenzeByDay.get(key)) missing.push(key);
    });
    return missing;
  }, [activeSheet, daysMeta, activePresenzeByDay]);

  const emptyCantiereDayKeys = useMemo(() => {
    const out = [];
    if (!activeSheet) return out;
    daysMeta.forEach(({ key }) => {
      const p = activePresenzeByDay.get(key);
      if (!p) return;
      if (getTipoPresenza(p) !== "lavorativa") return;
      if (!getCantiereLabel(p)) out.push(key);
    });
    return out;
  }, [activeSheet, daysMeta, activePresenzeByDay]);

  useEffect(() => {
    setAddingDayKey(null);
    setEditingPresenza(null);
    setAddError(null);
    setIsAdding(false);
    setDuplicatesDayKey(null);
    setDuplicatePresenze([]);
    setDuplicateError(null);
    setIsDeletingDuplicate(false);
  }, [monthFilter, activeSheet?.userId]);

  const openAddForDay = (dayKey) => {
    const activity =
      activeSheet?.userId && attivitaByUserAndDay.get(activeSheet.userId)
        ? attivitaByUserAndDay.get(activeSheet.userId).get(dayKey)
        : null;
    const t =
      activeSheet?.userId && timbratureQrByUserAndDay.get(activeSheet.userId)
        ? timbratureQrByUserAndDay.get(activeSheet.userId).get(dayKey)
        : null;
    setAddingDayKey(dayKey);
    setEditingPresenza(null);
    setAddError(null);
    setAddForm({
      tipoPresenza: "lavorativa",
      oraIngresso: t ? toTimeValue(t.openedAt) : "",
      oraUscita: t ? toTimeValue(t.closedAt) : "",
      cantiere: resolveProjectId(activity?.project, projectsData),
      mezzo: resolveMezzoId(activity?.mezzo, automezziData),
      durataPausa: "",
      codiceMalattia: "",
      note: "",
    });
  };

  const openEditForDay = (dayKey, presenza) => {
    setAddingDayKey(dayKey);
    setEditingPresenza(presenza || null);
    setAddError(null);

    const tipo =
      String(presenza?.tipoPresenza || "").trim() ||
      (presenza?.isFerie
        ? "ferie"
        : presenza?.isMalattia
          ? "malattia"
          : presenza?.isRiposo
            ? "riposo"
            : "lavorativa");
    const oraIngressoDate = toDateFromTimestampLike(presenza?.oraIngresso);
    const oraUscitaDate = toDateFromTimestampLike(presenza?.oraUscita);
    const durata =
      presenza?.durataPausa === null || presenza?.durataPausa === undefined
        ? ""
        : typeof presenza?.durataPausa === "number"
          ? String(presenza.durataPausa)
          : typeof presenza?.durataPausa === "string"
            ? presenza.durataPausa
            : "";

    setAddForm({
      tipoPresenza: tipo,
      oraIngresso: oraIngressoDate ? format(oraIngressoDate, "HH:mm") : "",
      oraUscita: oraUscitaDate ? format(oraUscitaDate, "HH:mm") : "",
      cantiere: resolveProjectId(presenza?.cantiere, projectsData),
      mezzo: resolveMezzoId(presenza?.mezzo, automezziData),
      durataPausa: durata,
      codiceMalattia: String(presenza?.codiceMalattia || "").trim(),
      note: String(presenza?.note || "").trim(),
    });
  };

  useEffect(() => {
    if (!addingDayKey || editingPresenza || !activeSheet?.userId) return;
    const t = activeTimbratureByDay.get(addingDayKey);
    const activity = activeAttivitaByDay.get(addingDayKey);
    const proposedIngresso = t ? toTimeValue(t.openedAt) : "";
    const proposedUscita = t ? toTimeValue(t.closedAt) : "";
    const proposedCantiere = resolveProjectId(activity?.project, projectsData);
    const proposedMezzo = resolveMezzoId(activity?.mezzo, automezziData);
    if (
      !proposedIngresso &&
      !proposedUscita &&
      !proposedCantiere &&
      !proposedMezzo
    ) {
      return;
    }
    setAddForm((prev) => ({
      ...prev,
      oraIngresso: prev.oraIngresso ? prev.oraIngresso : proposedIngresso,
      oraUscita: prev.oraUscita ? prev.oraUscita : proposedUscita,
      cantiere: prev.cantiere ? prev.cantiere : proposedCantiere,
      mezzo: prev.mezzo ? prev.mezzo : proposedMezzo,
    }));
  }, [
    addingDayKey,
    editingPresenza,
    activeAttivitaByDay,
    activeSheet?.userId,
    activeTimbratureByDay,
    automezziData,
    projectsData,
    toTimeValue,
  ]);

  useEffect(() => {
    if (!editingPresenza) return;
    const resolvedCantiere = resolveProjectId(
      editingPresenza?.cantiere,
      projectsData,
    );
    const resolvedMezzo = resolveMezzoId(editingPresenza?.mezzo, automezziData);
    if (!resolvedCantiere && !resolvedMezzo) return;
    setAddForm((prev) => ({
      ...prev,
      cantiere: prev.cantiere ? prev.cantiere : resolvedCantiere,
      mezzo: prev.mezzo ? prev.mezzo : resolvedMezzo,
    }));
  }, [editingPresenza, projectsData, automezziData]);

  const closeAdd = () => {
    setAddingDayKey(null);
    setEditingPresenza(null);
    setIsAdding(false);
    setAddError(null);
  };

  const openDuplicatesForDay = (dayKey) => {
    const items = activePresenzeByDayMulti.get(dayKey) || [];
    setAddingDayKey(null);
    setEditingPresenza(null);
    setDuplicatesDayKey(dayKey);
    setDuplicatePresenze(items);
    setDuplicateError(null);
    setIsDeletingDuplicate(false);
  };

  const closeDuplicates = () => {
    setDuplicatesDayKey(null);
    setDuplicatePresenze([]);
    setDuplicateError(null);
    setIsDeletingDuplicate(false);
  };

  const handleDeleteDuplicate = async (presenzaId) => {
    if (!presenzaId) return;
    if ((duplicatePresenze || []).length <= 1) return;
    try {
      setIsDeletingDuplicate(true);
      setDuplicateError(null);
      await database.collection("presenze").doc(presenzaId).delete();
      setDuplicatePresenze((prev) => prev.filter((p) => p?.id !== presenzaId));
    } catch (e) {
      setDuplicateError(e?.message || "Errore eliminazione presenza");
    } finally {
      setIsDeletingDuplicate(false);
    }
  };

  const handleSavePresenza = async () => {
    if (!activeSheet?.userId || !addingDayKey) return;

    const selectedDay =
      daysMeta.find((d) => d.key === addingDayKey)?.day || null;
    if (!selectedDay) return;

    const u = usersById.get(activeSheet.userId) || null;
    const userEmail = String(
      u?.email || u?.anagrafica?.emailGoogle || "",
    ).trim();

    try {
      setIsAdding(true);
      setAddError(null);

      const baseDateStr = format(selectedDay, "yyyy-MM-dd");
      const project = addForm.cantiere
        ? projectsData?.find((p) => p.id === addForm.cantiere)
        : null;
      const mezzo = addForm.mezzo
        ? automezziData?.find((m) => m.id === addForm.mezzo)
        : null;
      const presenzaData = {
        userId: activeSheet.userId,
        userEmail,
        dataRegistrazione: timestamp.fromDate(
          new Date(`${baseDateStr}T00:00:00`),
        ),
        tipoPresenza: addForm.tipoPresenza,
        oraIngresso:
          addForm.tipoPresenza === "lavorativa" && addForm.oraIngresso
            ? timestamp.fromDate(
                new Date(`${baseDateStr}T${addForm.oraIngresso}:00`),
              )
            : null,
        oraUscita:
          addForm.tipoPresenza === "lavorativa" && addForm.oraUscita
            ? timestamp.fromDate(
                new Date(`${baseDateStr}T${addForm.oraUscita}:00`),
              )
            : null,
        cantiere:
          addForm.tipoPresenza === "lavorativa" && addForm.cantiere
            ? {
                id: project?.id || null,
                nome: project?.nome || "",
                codice: project?.codice || null,
              }
            : null,
        mezzo:
          addForm.tipoPresenza === "lavorativa" && addForm.mezzo
            ? {
                id: mezzo?.id || null,
                modello: mezzo?.modello || "",
                targa: mezzo?.targa || "",
              }
            : null,
        durataPausa:
          addForm.tipoPresenza === "lavorativa"
            ? addForm.durataPausa === ""
              ? ""
              : Number.isFinite(Number(addForm.durataPausa))
                ? Number(addForm.durataPausa)
                : ""
            : null,
        isFerie: addForm.tipoPresenza === "ferie",
        isMalattia: addForm.tipoPresenza === "malattia",
        isRiposo: addForm.tipoPresenza === "riposo",
        codiceMalattia:
          addForm.tipoPresenza === "malattia"
            ? String(addForm.codiceMalattia || "").trim()
            : null,
        note: String(addForm.note || "").trim(),
        updatedAt: timestamp.fromDate(new Date()),
      };

      if (editingPresenza?.id) {
        await database
          .collection("presenze")
          .doc(editingPresenza.id)
          .update(presenzaData);
      } else {
        const created = await database.collection("presenze").add({
          ...presenzaData,
          hasTimbratura: false,
          createdAt: timestamp.fromDate(new Date()),
        });
        if (!created?.id) {
          throw new Error("PRESENZA_NOT_CREATED");
        }
      }

      closeAdd();
    } catch (e) {
      setAddError(e?.message || "Errore salvataggio presenza");
      setIsAdding(false);
    }
  };

  return (
    <div className="presenze-check-container">
      <h2 className="page-title">Verifica Presenze</h2>

      <div className="pc-toolbar">
        <div className="pc-toolbar-left">
          <div className="pc-field pc-field--month">
            <Select
              classNamePrefix="react-select"
              styles={selectModalStyles}
              menuPortalTarget={document.body}
              options={monthOptions}
              value={monthOptions.find((o) => o.value === monthFilter) || null}
              onChange={(opt) => setMonthFilter(opt ? opt.value : "")}
              isClearable={false}
              isSearchable={true}
              placeholder="Seleziona mese"
            />
          </div>

          {userSheets.length > 0 && (
            <div className="pc-field pc-field--user">
              <Select
                classNamePrefix="react-select"
                styles={selectModalStyles}
                menuPortalTarget={document.body}
                options={userSheets.map((s) => ({
                  value: s.userId,
                  label: getUserLabel(s.user),
                }))}
                value={
                  activeSheet
                    ? {
                        value: activeSheet.userId,
                        label: getUserLabel(activeSheet.user),
                      }
                    : null
                }
                onChange={(opt) => {
                  const idx = userSheets.findIndex(
                    (s) => s.userId === opt?.value,
                  );
                  if (idx >= 0) setActiveIndex(idx);
                }}
                isClearable={false}
                isSearchable={true}
                placeholder="Seleziona dipendente"
              />
            </div>
          )}
        </div>

        <div className="pc-toolbar-right pc-pdf">
          <button
            className="btn-download-pdf"
            type="button"
            disabled={
              !activeSheet ||
              !periodo ||
              (activeSheet?.presenze?.length || 0) === 0
            }
            onClick={() => {
              try {
                const safeUser = (getUserLabel(activeSheet.user) || "utente")
                  .replace(/[^a-zA-Z0-9_-]/g, "_")
                  .substring(0, 50);
                const fileName = `presenze_${safeUser}_${periodoLabel || "periodo"}.pdf`;
                downloadPresenzePdf({
                  presenze: ordinaPresenze(activeSheet.presenze),
                  user: activeSheet.user,
                  periodo,
                  fileName,
                  reperibilita: getReperibilitaForUser(activeSheet.userId),
                  projects: projectsData || [],
                  documenti: tuttiDocumenti || [],
                  isAdmin: true,
                  confirmedBonuses:
                    allConfirmedBonuses[activeSheet.userId] ?? null,
                });
              } catch (e) {
                console.error("Errore PDF singolo:", e);
              }
            }}
          >
            📥 PDF Dipendente
          </button>

          <button
            className="btn-download-pdf"
            type="button"
            disabled={
              !periodo || userSheets.length === 0 || generatingMultiUserPdf
            }
            onClick={async () => {
              try {
                setGeneratingMultiUserPdf(true);
                const usersData = userSheets
                  .filter((s) => (s.presenze?.length || 0) > 0)
                  .map((s) => ({
                    ...s,
                    presenze: ordinaPresenze(s.presenze),
                    reperibilita: getReperibilitaForUser(s.userId),
                    confirmedBonuses: allConfirmedBonuses[s.userId] ?? null,
                  }));
                const fileName = `presenze_tutti_dipendenti_${periodoLabel || "periodo"}.pdf`;
                await downloadPresenzeMultiPdf({
                  usersData,
                  periodo,
                  fileName,
                  projects: projectsData || [],
                  documenti: tuttiDocumenti || [],
                  isAdmin: true,
                });
              } catch (e) {
                console.error("Errore PDF multi:", e);
              } finally {
                setGeneratingMultiUserPdf(false);
              }
            }}
          >
            {generatingMultiUserPdf ? "Generazione..." : "📥 PDF Completo"}
          </button>
        </div>
      </div>

      <div className="pc-subtoolbar">
        <div className="pc-nav">
          <button
            className="btn"
            type="button"
            onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
            disabled={userSheets.length === 0 || activeIndex === 0}
          >
            Precedente
          </button>
          <button
            className="btn"
            type="button"
            onClick={() =>
              setActiveIndex((i) => Math.min(userSheets.length - 1, i + 1))
            }
            disabled={
              userSheets.length === 0 || activeIndex >= userSheets.length - 1
            }
          >
            Successivo
          </button>
          <span className="pc-counter">
            {userSheets.length === 0
              ? "0/0"
              : `${activeIndex + 1}/${userSheets.length}`}
          </span>
        </div>

        <div className="pc-completion">
          <span className="pc-completion-label">Verifica mese</span>
          <span
            className={
              activeSheet &&
              missingDayKeys.length === 0 &&
              emptyCantiereDayKeys.length === 0
                ? "pc-completion-value ok"
                : "pc-completion-value warn"
            }
          >
            {activeSheet
              ? missingDayKeys.length === 0 && emptyCantiereDayKeys.length === 0
                ? "OK (presenze complete)"
                : [
                    missingDayKeys.length > 0
                      ? `Mancano ${missingDayKeys.length} giorni`
                      : null,
                    emptyCantiereDayKeys.length > 0
                      ? `Cantiere vuoto in ${emptyCantiereDayKeys.length} giorni`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" • ")
              : "-"}
          </span>
        </div>
      </div>

      {presenzeError && (
        <div className="error-msg">Errore: {presenzeError}</div>
      )}

      {!presenzeError && monthFilter && userSheets.length === 0 && (
        <p className="pc-empty">
          Nessuna presenza trovata per il mese selezionato.
        </p>
      )}

      {activeSheet && (
        <div className="pc-sheet-card">
          <div className="pc-sheet-title-row">
            <span className="pc-sheet-title-label">Dipendente</span>
            <span className="pc-sheet-title-name">
              {getUserLabel(activeSheet.user)}
            </span>
          </div>
          <div className="pc-table-wrap">
            <table className="ferie-table" style={{ minWidth: 920 }}>
              <thead>
                <tr>
                  <th>Giorno</th>
                  <th>Tipo</th>
                  <th>Ingresso</th>
                  <th>Uscita</th>
                  <th>Cantiere</th>
                  <th>Note</th>
                  <th></th>
                  <th>Timbratura</th>
                  <th>Doppie</th>
                </tr>
              </thead>
              <tbody>
                {daysMeta.map(({ day, key }) => {
                  const p = activePresenzeByDay.get(key);
                  const t = activeTimbratureByDay.get(key);
                  const dupCount = (activePresenzeByDayMulti.get(key) || [])
                    .length;
                  const hasDuplicates = dupCount > 1;
                  const isMissing = !p;
                  const isEmptyCantiere =
                    !isMissing &&
                    getTipoPresenza(p) === "lavorativa" &&
                    !getCantiereLabel(p);
                  return (
                    <tr
                      key={key}
                      style={
                        isMissing
                          ? { background: "rgba(231, 76, 60, 0.08)" }
                          : isEmptyCantiere
                            ? { background: "rgba(241, 196, 15, 0.12)" }
                            : undefined
                      }
                    >
                      <td>{format(day, "EEE dd/MM", { locale: it })}</td>
                      <td>
                        {isMissing ? "MANCANTE" : renderCell(p?.tipoPresenza)}
                      </td>
                      <td>{renderCell(p?.oraIngresso, "time")}</td>
                      <td>{renderCell(p?.oraUscita, "time")}</td>
                      <td>
                        {isMissing
                          ? "-"
                          : renderCell(p?.cantiere?.nome || p?.cantiere)}
                      </td>
                      <td
                        style={{
                          maxWidth: 420,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {renderCell(p?.note)}
                      </td>
                      <td>
                        {isMissing ? (
                          <button
                            className="btn"
                            type="button"
                            onClick={() => openAddForDay(key)}
                          >
                            Inserisci
                          </button>
                        ) : (
                          <button
                            className="btn"
                            type="button"
                            onClick={() => openEditForDay(key, p)}
                          >
                            Modifica
                          </button>
                        )}
                      </td>
                      <td>
                        {!t
                          ? "-"
                          : t?.openedAt && t?.closedAt
                            ? "Completa"
                            : t?.openedAt
                              ? "Ingresso"
                              : "Presente"}
                      </td>
                      <td>
                        {hasDuplicates ? (
                          <button
                            className="btn"
                            type="button"
                            onClick={() => openDuplicatesForDay(key)}
                          >
                            {dupCount} ⚠︎
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {addingDayKey && activeSheet && (
        <div className="pc-modal-overlay" onClick={closeAdd}>
          <div className="pc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pc-modal-header">
              <h3 className="pc-modal-title">
                {editingPresenza ? "Modifica" : "Inserisci"} presenza{" "}
                {addingDayKey} — {getUserLabel(activeSheet.user)}
              </h3>
              <button
                className="pc-modal-close"
                type="button"
                onClick={closeAdd}
                disabled={isAdding}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            {addError && <div className="error-msg">{addError}</div>}

            <div className="pc-modal-grid">
              <label className="pc-modal-field">
                <span className="pc-modal-label">Tipo presenza</span>
                <Select
                  classNamePrefix="react-select"
                  styles={selectModalStyles}
                  menuPortalTarget={document.body}
                  options={tipoPresenzaOptions}
                  value={
                    tipoPresenzaOptions.find(
                      (o) => o.value === addForm.tipoPresenza,
                    ) || null
                  }
                  onChange={(opt) =>
                    setAddForm((p) => ({
                      ...p,
                      tipoPresenza: opt ? opt.value : "lavorativa",
                    }))
                  }
                  isClearable={false}
                  isSearchable={false}
                />
              </label>

              <label className="pc-modal-field">
                <span className="pc-modal-label">Cantiere</span>
                <Select
                  name="cantiere"
                  value={
                    projectOptions.find((o) => o.value === addForm.cantiere) ||
                    null
                  }
                  onChange={(selectedOption) =>
                    setAddForm((p) => ({
                      ...p,
                      cantiere: selectedOption ? selectedOption.value : "",
                    }))
                  }
                  options={projectOptions}
                  placeholder="Seleziona cantiere"
                  isClearable
                  isDisabled={addForm.tipoPresenza !== "lavorativa"}
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={selectModalStyles}
                />
              </label>

              <label className="pc-modal-field">
                <span className="pc-modal-label">Mezzo</span>
                <Select
                  name="mezzo"
                  value={
                    mezzoOptions.find((o) => o.value === addForm.mezzo) || null
                  }
                  onChange={(selectedOption) =>
                    setAddForm((p) => ({
                      ...p,
                      mezzo: selectedOption ? selectedOption.value : "",
                    }))
                  }
                  options={mezzoOptions}
                  placeholder="Seleziona mezzo"
                  isClearable
                  isDisabled={addForm.tipoPresenza !== "lavorativa"}
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={selectModalStyles}
                />
              </label>

              <label className="pc-modal-field">
                <span className="pc-modal-label">Ingresso</span>
                <input
                  type="time"
                  value={addForm.oraIngresso}
                  onChange={(e) =>
                    setAddForm((p) => ({ ...p, oraIngresso: e.target.value }))
                  }
                  disabled={addForm.tipoPresenza !== "lavorativa"}
                />
              </label>

              <label className="pc-modal-field">
                <span className="pc-modal-label">Uscita</span>
                <input
                  type="time"
                  value={addForm.oraUscita}
                  onChange={(e) =>
                    setAddForm((p) => ({ ...p, oraUscita: e.target.value }))
                  }
                  disabled={addForm.tipoPresenza !== "lavorativa"}
                />
              </label>

              <label className="pc-modal-field">
                <span className="pc-modal-label">Durata Pausa</span>
                <Select
                  name="durataPausa"
                  value={
                    pausaOptions.find((o) => o.value === addForm.durataPausa) ||
                    null
                  }
                  onChange={(selectedOption) =>
                    setAddForm((p) => ({
                      ...p,
                      durataPausa: selectedOption ? selectedOption.value : "",
                    }))
                  }
                  options={pausaOptions}
                  placeholder="Seleziona durata pausa"
                  isClearable
                  isDisabled={addForm.tipoPresenza !== "lavorativa"}
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={selectModalStyles}
                />
              </label>

              <label className="pc-modal-field">
                <span className="pc-modal-label">Protocollo malattia</span>
                <input
                  type="text"
                  value={addForm.codiceMalattia}
                  onChange={(e) =>
                    setAddForm((p) => ({
                      ...p,
                      codiceMalattia: e.target.value,
                    }))
                  }
                  disabled={addForm.tipoPresenza !== "malattia"}
                />
              </label>

              <label className="pc-modal-field pc-modal-field--full">
                <span className="pc-modal-label">Note</span>
                <textarea
                  rows={3}
                  value={addForm.note}
                  onChange={(e) =>
                    setAddForm((p) => ({ ...p, note: e.target.value }))
                  }
                />
              </label>
            </div>

            <div className="pc-modal-actions">
              <button
                className="btn"
                type="button"
                onClick={closeAdd}
                disabled={isAdding}
              >
                Annulla
              </button>
              <button
                className="btn"
                type="button"
                onClick={handleSavePresenza}
                disabled={isAdding}
              >
                {isAdding
                  ? "Salvataggio..."
                  : editingPresenza
                    ? "Aggiorna"
                    : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {duplicatesDayKey && activeSheet && (
        <div className="pc-modal-overlay" onClick={closeDuplicates}>
          <div className="pc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pc-modal-header">
              <h3 className="pc-modal-title">
                Presenze doppie {duplicatesDayKey} —{" "}
                {getUserLabel(activeSheet.user)}
              </h3>
              <button
                className="pc-modal-close"
                type="button"
                onClick={closeDuplicates}
                disabled={isDeletingDuplicate}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            {duplicateError && (
              <div className="error-msg">{duplicateError}</div>
            )}

            {duplicatePresenze.length <= 1 ? (
              <p className="pc-empty">Non ci sono più presenze doppie.</p>
            ) : (
              <div className="pc-table-wrap">
                <table className="ferie-table" style={{ minWidth: 820 }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Tipo</th>
                      <th>Ingresso</th>
                      <th>Uscita</th>
                      <th>Cantiere</th>
                      <th>Note</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {duplicatePresenze.map((p, idx) => (
                      <tr key={p?.id || idx}>
                        <td>{idx + 1}</td>
                        <td>{renderCell(getTipoPresenza(p))}</td>
                        <td>{renderCell(p?.oraIngresso, "time")}</td>
                        <td>{renderCell(p?.oraUscita, "time")}</td>
                        <td>{renderCell(p?.cantiere?.nome || p?.cantiere)}</td>
                        <td
                          style={{
                            maxWidth: 360,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {renderCell(p?.note)}
                        </td>
                        <td>
                          <button
                            className="btn"
                            type="button"
                            disabled={
                              isDeletingDuplicate ||
                              duplicatePresenze.length <= 1 ||
                              !p?.id
                            }
                            onClick={() => handleDeleteDuplicate(p.id)}
                          >
                            Elimina
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="pc-modal-actions">
              <button
                className="btn"
                type="button"
                onClick={closeDuplicates}
                disabled={isDeletingDuplicate}
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
